<script>
  import { slide } from 'svelte/transition'
  import { auth } from '../../services/auth.js'
  import Alert from '../../components/Alert.svelte'
  import { user } from '../../models/user.js'
  import { onMount } from 'svelte'
  import { track } from '../../services/analytics.js'
  import { pendingPlan, pendingEmail } from '../../models/pendingPlan.js'

  var submitting = false
  var success = $user && $user.username ? true : false
  var resendSuccess = false
  var error = false
  var errorMsg = ''
  let verifyCode = null
  let referrer = null
  let prefillEmail = ''

  onMount(async () => {
    let urlParams = new URLSearchParams(window.location.search)
    verifyCode = urlParams.get('verifyCode')
    referrer = urlParams.get('referrer')
    prefillEmail = pendingEmail.get()
  })

  function validateForm() {
    var email = document.getElementById('email')
    var pass = document.getElementById('password')

    if (!email.checkValidity()) {
      errorMsg = 'You have entered an invalid email address!'
      return false
    }

    if (!pass.checkValidity()) {
      errorMsg = 'Your password is required!'
      return false
    }

    if (verifyCode !== null && verifyCode === '') {
      errorMsg = 'Please enter your verification code from your email'
      return false
    }

    return true
  }

  function signIn(e) {
    if (e && e.key !== 'Enter' && e.type !== 'click') return
    errorMsg = null

    if (validateForm()) {
      submitting = true

      if (verifyCode !== null) {
        auth.verifyAndAuthenticate(verifyCode, signedIn)
      } else {
        auth.authenticate(signedIn)
      }
    }
  }

  function signedIn(result, message) {
    submitting = false

    // User has been logged in!
    if (result) {
      success = true
      user.login()

      if (verifyCode !== null) track('email_verified')
      pendingEmail.clear()

      // they picked a plan before signing in; checkout adds it to the cart
      if (pendingPlan.get()) {
        window.location.href = '/checkout'
        return
      }

      // document.referrer doesn't work well on SPAs. This will be the URL you
      // first loaded the page on, not the previous page. You can't view
      // history for privacy reasons. So we have to do it this way.
      let overrides = [
        'logout',
        'register',
        'resetPass',
        'reset',
        '/',
        '/logout',
      ]

      if (!referrer || overrides.includes(referrer)) {
        window.location.href = '/'
      } else if (referrer === '/cart') {
        // force a page load for cart validation to run
        window.location.href = '/cart'
      } else if (
        verifyCode !== null &&
        referrer.startsWith('/') &&
        !referrer.startsWith('//')
      ) {
        // came here from sign up, so going "back" would land on /register
        window.location.href = referrer
      } else {
        window.history.back()
      }

      // User has not verified their email yet!
    } else if (message.indexOf('not confirmed') > 0) {
      errorMsg = 'Your email has not been verified!'

      // Something else went wrong!
    } else {
      errorMsg = message
    }
  }

  function resendVerification() {
    auth.resendVerification(resendResult)
    verifyCode = ''
  }

  function resendResult(result, error) {
    success = false

    if (result) {
      resendSuccess = true
    } else {
      errorMsg = error
    }
  }
</script>

<style type="text/less">
  .container {
    padding: var(--spacing-sm);
  }
  @media (min-width: 800px) {
    .container {
      padding: var(--spacing);
    }
  }
</style>

<div class="container">
  <div class="user-cont" transition:slide>
    <div class="user-content">
      <h1 class="user-title">Sign In</h1>

      <div class="user-form">
        {#if errorMsg}
          <p>
            <Alert type="danger" icon="warning">
              <div class="flex-align-center flex-space-between">
                {errorMsg}
                {#if errorMsg.indexOf('verified!') > -1 || errorMsg.indexOf('Invalid verification code') > -1}
                  <button class="btn btn-link" on:click={resendVerification}>
                    Resend verification email
                  </button>
                {/if}
              </div>
            </Alert>
          </p>
        {/if}

        {#if success}
          <Alert type="success" icon="check">You are signed in!</Alert>

          <p class="text-right">
            <a href="/" rel="external">
              Return to site
              <svg class="icon">
                <use href="/icons.svg#keyboard_arrow_right" />
              </svg>
            </a>
          </p>
        {:else}
          {#if resendSuccess}
            <Alert type="success" icon="check">Verification email sent!</Alert>
          {/if}
          {#if verifyCode !== null}
            <div>
              <p>
                <b>
                  Please check your email for your verification code. If you
                  have not received it, check your spam folder or
                  <button class="btn btn-link" on:click={resendVerification}>
                    click here to resend the verification email.
                  </button>
                </b>
              </p>
            </div>
          {/if}
          <div class="form-field">
            <label class="form-field-label" for="email">Email</label>
            <input
              class="form-input"
              type="email"
              id="email"
              name="email"
              value={prefillEmail}
              required />
          </div>

          <div class="form-field">
            <label class="form-field-label" for="password">Password</label>
            <input
              class="form-input"
              type="password"
              id="password"
              name="password"
              on:keydown={signIn}
              required />
          </div>

          {#if verifyCode !== null}
            <div class="form-field">
              <label class="form-field-label" for="verifyCode">
                One Time Verification Code
              </label>
              <input
                class="form-input"
                id="verifyCode"
                name="verifyCode"
                bind:value={verifyCode}
                on:keydown={signIn}
                required />
            </div>
          {/if}

          <div class="form-field">
            <button class="btn btn-expanded" on:click|preventDefault={signIn}>
              {#if submitting}
                <svg class="icon spin">
                  <use href="/icons.svg#circle_notch" />
                </svg>
                Signing In...
              {:else if verifyCode !== null}
                Verify and Sign In
              {:else}Sign In{/if}
            </button>
          </div>
        {/if}
      </div>

      {#if !success}
        <p class="user-form-links">
          <a href="/reset">Forgot password?</a>
          |
          <a href="/register">Not a member? Sign up!</a>
        </p>
      {/if}
    </div>
  </div>
</div>
